import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  RadioGroup,
  Radio,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
  Button,
  Tooltip,
} from '@material-ui/core';
import { SwapHoriz } from '@material-ui/icons';
import Loading from 'components/Loading';
import { TRANSFER_TITLE_SITE_DOMAIN } from 'queries/titleContent';
import { Toast } from 'components/Toast';

import { UserService } from 'services/User/UserService';
import { useMutation } from '@apollo/client';

interface ITransferDomainProps {
  selectedTitles: Map<string, ITitle>;
  refetch: any;
}

const TransferDomain: React.FC<ITransferDomainProps> = ({
  selectedTitles,
  refetch,
}) => {
  const sites = UserService.useSitesFromAccess();

  const [open, setOpen] = useState(false);
  const [selectedSiteDomain, setSelectedSiteDomain] = useState('');
  const [openLoading, setOpenLoading] = useState(false);
  const radioGroupRef = useRef<HTMLElement>();

  const handleEntering = () => {
    if (radioGroupRef.current) {
      radioGroupRef.current.focus();
    }
  };

  const handleClose = () => {
    setOpen(!open);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSiteDomain(event.target.value);
  };

  const [transferSiteDomain] = useMutation(TRANSFER_TITLE_SITE_DOMAIN, {
    onCompleted: (data) => {
      const results = data.transferSiteDomain;

      const successCount = results.filter((r: any) => r.success).length;
      const failedCount = results.length - successCount;

      if (failedCount === 0) {
        Toast(`${successCount} title(s) transferred successfully.`, 'success');
      } else if (successCount === 0) {
        Toast(`Failed to transfer ${failedCount} title(s).`, 'error');
      } else {
        Toast(
          `${successCount} title(s) transferred successfully, ${failedCount} failed.`,
          'warning',
        );
      }

      refetch();
    },
    onError: (error) => {
      Toast(`Error during domain transfer: ${error.message}`, 'error');
    },
  });

  const handleConfirm = async () => {
    setOpen(false);
    if (!selectedSiteDomain) return;

    setOpenLoading(true);

    try {
      const titleIds = Array.from(selectedTitles.keys());

      await transferSiteDomain({
        variables: {
          titleIds,
          siteDomainDestination: selectedSiteDomain,
        },
      });
    } catch (error) {
      Toast('An unexpected error occurred during transfer.', 'error');
    } finally {
      setOpenLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Transfer to domain">
        <IconButton
          color="primary"
          aria-label="transfer domain"
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
        >
          <SwapHoriz />
        </IconButton>
      </Tooltip>

      <Loading openLoading={openLoading} />
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="xs"
        onEntering={handleEntering}
        aria-labelledby="transfer-domain-title"
        onClose={handleClose}
        open={open}
      >
        <DialogTitle id="transfer-domain-title">Transfer to domain</DialogTitle>
        <DialogContent dividers>
          <RadioGroup
            ref={radioGroupRef}
            aria-label="site-domain"
            name="site-domain"
            value={selectedSiteDomain}
            onChange={handleChange}
          >
            {sites && sites.length > 0 ? (
              sites.map((site) => (
                <FormControlLabel
                  key={site}
                  value={site}
                  control={<Radio />}
                  label={site}
                />
              ))
            ) : (
              <FormControlLabel
                value=""
                control={<Radio />}
                label="No sites available"
                disabled
              />
            )}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            disabled={!selectedSiteDomain}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransferDomain;
